/**
 * The outgoing queue (SPEC §8.2).
 *
 * Everything a child produces *inside a tutoring session* goes through here — a
 * message, an answer — plus the absence declaration, whether the network is up
 * or not. There is no "online path" and "offline path": one path, and a queue
 * that happens to drain immediately when it can. Code with two paths only ever
 * exercises one.
 *
 * **Le journal du jour n'y est pas**, et c'est délibéré (SPEC §5.5, §8.1). Il
 * y était : sous le nom `daily-report`, avec l'identifiant de séance pour clé,
 * alors que `sendItem` commence par `if (!item.tutorSessionId) return;`. Un
 * journal écrit hors séance était donc silencieusement jeté, et l'écran disait
 * « envoyé ». Il part maintenant par sa route ; hors ligne il échoue, le texte
 * reste à l'écran, et c'est dit en français.
 *
 * Idempotence is not retry logic, it is a value: `client_id` is minted **once**,
 * when the child taps, and travels with every replay. `core-api` returns the
 * same event for the same `client_id` (`events.find_by_client_id`), so a queue
 * replayed three times produces one observation.
 */

import { ApiError } from "@/api/errors";

export type OutboxKind = "session.message" | "absence";

export type OutboxItem = {
  /** The idempotency key. Minted at creation, never regenerated on retry. */
  clientId: string;
  kind: OutboxKind;
  /** Which tutoring session this belongs to, when it belongs to one. */
  tutorSessionId: string | null;
  text: string;
  /** LaTeX and MathJSON of a mathematical answer, when there is one. */
  math: { latex: string; mathjson: string | null } | null;
  /**
   * Le temps passé sur l'exercice, en millisecondes (`child/chrono`).
   *
   * Facultatif, et il doit le rester : les articles écrits avant le 30 août 2026
   * dorment dans la même base IndexedDB, sans cette clé, et une file hors ligne
   * ne se migre pas — ce sont les mots d'un enfant, on ne les jette pas pour
   * changer un champ. `undefined` se lit « durée inconnue », comme partout.
   */
  durationMs?: number | null;
  createdAt: string;
  /**
   * Rang d'arrivée. `createdAt` ne suffit pas : deux messages écrits dans la
   * même milliseconde se départageaient par l'ordre de la base, c'est-à-dire au
   * hasard d'un UUID — et une réponse pouvait partir avant la question.
   */
  seq: number;
  attempts: number;
  lastError: string | null;
  /**
   * `failed` means the server refused this permanently (a 404, a 422). It stays
   * in the queue anyway: these are the child's own words, and deleting them
   * because a server said no means she retypes them. `drain` skips failed items
   * so one refusal never jams what follows; `retry` puts one back in line.
   */
  state: "pending" | "failed";
};

const DB_NAME = "tuteur-outbox";
const DB_VERSION = 1;
const STORE = "items";

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "clientId" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

async function transact<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const request = work(tx.objectStore(STORE));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export function newClientId(): string {
  return crypto.randomUUID();
}

//: Compteur de rang, réhydraté depuis la file au premier usage — un rechargement
//: de page ne doit pas replacer un nouveau message devant ceux qui attendaient.
let nextSeq: number | null = null;

async function takeSeq(): Promise<number> {
  if (nextSeq === null) {
    const items = await transact<OutboxItem[]>(
      "readonly",
      (store) => store.getAll() as IDBRequest<OutboxItem[]>,
    );
    nextSeq = items.reduce((highest, item) => Math.max(highest, item.seq ?? 0), 0) + 1;
  }
  return nextSeq++;
}

export async function enqueue(
  item: Omit<OutboxItem, "createdAt" | "seq" | "attempts" | "lastError" | "state">,
): Promise<OutboxItem> {
  const stored: OutboxItem = {
    ...item,
    createdAt: new Date().toISOString(),
    seq: await takeSeq(),
    attempts: 0,
    lastError: null,
    state: "pending",
  };
  await transact("readwrite", (store) => store.put(stored));
  notify();
  return stored;
}

export async function list(): Promise<OutboxItem[]> {
  const items = await transact<OutboxItem[]>(
    "readonly",
    (store) => store.getAll() as IDBRequest<OutboxItem[]>,
  );
  return items.sort(
    (a, b) => (a.seq ?? 0) - (b.seq ?? 0) || a.createdAt.localeCompare(b.createdAt),
  );
}

export async function remove(clientId: string): Promise<void> {
  await transact("readwrite", (store) => store.delete(clientId));
  notify();
}

async function recordAttempt(item: OutboxItem, error: string | null): Promise<void> {
  await transact("readwrite", (store) =>
    store.put({ ...item, attempts: item.attempts + 1, lastError: error }),
  );
  notify();
}

/** Refused for good: keep the text, stop trying, let the child decide. */
async function markFailed(item: OutboxItem, error: string): Promise<void> {
  await transact("readwrite", (store) =>
    store.put({ ...item, attempts: item.attempts + 1, lastError: error, state: "failed" }),
  );
  notify();
}

/** Put a failed item back in line — what the "réessayer" button calls. */
export async function retry(clientId: string): Promise<void> {
  const items = await list();
  const item = items.find((candidate) => candidate.clientId === clientId);
  if (!item) return;
  await transact("readwrite", (store) =>
    store.put({ ...item, state: "pending", lastError: null }),
  );
  notify();
}

/**
 * Re-address every queued session message to a new tutoring session.
 *
 * A tutoring session can die under a queued message — the browser kept an id
 * from a previous child or a reset database. Re-pointing is what lets the
 * message survive that: the child's words belong to the child, not to the
 * session identifier they happened to be written under.
 */
export async function repoint(tutorSessionId: string): Promise<number> {
  const items = await list();
  const stale = items.filter(
    (item) => item.kind === "session.message" && item.tutorSessionId !== tutorSessionId,
  );
  for (const item of stale) {
    await transact("readwrite", (store) =>
      store.put({ ...item, tutorSessionId, state: "pending", lastError: null }),
    );
  }
  if (stale.length) notify();
  return stale.length;
}

// --- Subscription -----------------------------------------------------------
// The queue is a piece of state the child can see (SPEC §8.3): "envoyé" and "en
// attente d'envoi" are different words on screen, always.

type Listener = () => void;
const listeners = new Set<Listener>();

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(): void {
  for (const listener of listeners) listener();
}

// --- Draining ---------------------------------------------------------------

export type Sender = (item: OutboxItem) => Promise<void>;

let draining = false;

/**
 * Try to send everything, oldest first.
 *
 * Stops at the first transport failure: order matters inside a conversation,
 * and a second message that arrives before the first one it answers reads as
 * nonsense. A refusal that is *not* a transport failure (422, 404) **marks the
 * item failed and moves on**: replaying it forever would jam the queue behind
 * it, and deleting it would make a child retype what she already wrote.
 */
export async function drain(send: Sender): Promise<void> {
  if (draining || !navigator.onLine) return;
  draining = true;
  try {
    for (const item of await list()) {
      if (item.state === "failed") continue;
      try {
        await send(item);
        await remove(item.clientId);
      } catch (error) {
        const failure = error instanceof ApiError ? error : null;
        if (failure && !failure.isTransport && !failure.needsLogin) {
          await markFailed(item, failure.message);
          continue;
        }
        await recordAttempt(item, failure?.message ?? "envoi impossible");
        return;
      }
    }
  } finally {
    draining = false;
  }
}
